import { type ReactNode, useId, useState } from 'react';

import { IconAlert, IconCheck, IconChevronDown } from '../icons';
import {
  BUSY_SPINNER_CLASS,
  PRESS_ROW_CLASS,
  PRESS_TEXT_CLASS,
  TAP_TARGET_CLASS,
} from '../styles/tokens';
import { cx } from './cx';
import styles from './Feedback.module.css';

export type ToastTone = 'success' | 'error';

/** Figma M/Toast 46:364 — a flat pill with a leading glyph. */
export function Toast({ tone = 'success', children }: { tone?: ToastTone; children: ReactNode }) {
  return (
    <div
      role="status"
      className={cx(styles.toast, tone === 'error' ? styles.toastError : styles.toastSuccess)}
    >
      {tone === 'error' ? <IconAlert size={18} /> : <IconCheck size={18} />}
      <span className={styles.toastLabel}>{children}</span>
    </div>
  );
}

/**
 * Inline banner for form-level results (a failed sign-in, a pending device grant). Errors get
 * `role="alert"` so a screen reader announces them without the user hunting for the field.
 */
export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error';
  children: ReactNode;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx(styles.banner, tone === 'error' ? styles.bannerError : styles.bannerInfo)}
    >
      {tone === 'error' ? <IconAlert size={18} /> : <IconCheck size={18} />}
      <span className={styles.bannerText}>{children}</span>
    </div>
  );
}

/** Cyan text link ("¿Olvidaste tu contraseña?" 48:110, "Restablecer contraseña" 49:424). */
export function TextLink({
  children,
  onClick,
  align = 'end',
  ...rest
}: {
  children: ReactNode;
  onClick: () => void;
  align?: 'end' | 'center';
  'data-testid'?: string;
}) {
  return (
    <button
      type="button"
      className={cx(
        styles.link,
        align === 'center' && styles.linkCenter,
        TAP_TARGET_CLASS,
        PRESS_TEXT_CLASS,
      )}
      onClick={onClick}
      data-testid={rest['data-testid']}
    >
      {children}
    </button>
  );
}

/** Muted centred small print ("Al continuar aceptas…" 48:255). */
export function FinePrint({ children }: { children: ReactNode }) {
  return <p className={styles.terms}>{children}</p>;
}

/** Collapsed "¿Tienes un código?" row of M-A4 (48:246). */
export function Disclosure({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className={cx(styles.disclosure, open && styles.disclosureOpen)}>
      <button
        type="button"
        className={cx(styles.disclosureButton, PRESS_ROW_CLASS)}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span className={styles.disclosureIcon}>
          <IconChevronDown size={20} />
        </span>
      </button>
      {open ? (
        <div id={panelId} className={styles.disclosurePanel}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Loading indicator. The default is the centred 28 px block a screen shows while its data is on
 * the way; `label` is its accessible name (`role="status"`), never drawn.
 *
 * `inline` is the 16 px busy spinner *next to* its label, which stays visible ("Comprobando…"
 * under the code box): a status line that is still moving reads as work in progress, a bare
 * sentence reads as a frozen screen.
 */
export function Spinner({ label, inline = false }: { label: string; inline?: boolean }) {
  if (inline) {
    return (
      <span className={styles.spinnerInline} role="status">
        <span className={BUSY_SPINNER_CLASS} aria-hidden="true" data-testid="inline-spinner" />
        <span>{label}</span>
      </span>
    );
  }
  return (
    <div className={styles.spinnerWrap} role="status" aria-label={label}>
      <span className={styles.spinner} />
    </div>
  );
}
